import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import { API_BASE_URL } from '../../config';

export default function ProfileSettings() {
  const { user, getAccessTokenSilently } = useAuth0();
  const { currentOrganization, currentSchool } = useTenant();
  
  // Profile state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [originalName, setOriginalName] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Load current user data from database
  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (user?.sub) {
          const token = await getAccessTokenSilently();
          
          // Use the auth endpoint to get user by Auth0 ID
          const response = await fetch(`${API_BASE_URL}/api/auth/user/${user.sub}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const userData = await response.json();
            
            if (userData) {
              setProfileData({
                name: userData.name || user?.name || '',
                email: userData.email || user?.email || ''
              });
              setOriginalName(userData.name || user?.name || '');
            }
          } else {
            // Fallback to Auth0 data if API fails
            throw new Error('Failed to fetch user data from API');
          }
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        // Fallback to Auth0 data
        setProfileData({
          name: user?.name || '',
          email: user?.email || ''
        });
        setOriginalName(user?.name || '');
      }
    };

    loadUserData();
  }, [user, getAccessTokenSilently]);

  // Check for changes
  useEffect(() => {
    const nameChanged = profileData.name !== originalName;
    setHasChanges(nameChanged);
  }, [profileData.name, originalName]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (savedMessage || error) {
      const timer = setTimeout(() => {
        setSavedMessage('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [savedMessage, error]);

  const handleNameChange = (e) => {
    setProfileData(prev => ({
      ...prev,
      name: e.target.value
    }));
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    try {
      setSaving(true);
      setError('');
      
      const token = await getAccessTokenSilently();
      
      // Update user profile using the auth endpoint
      const response = await fetch(`${API_BASE_URL}/api/auth/user/${user.sub}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: profileData.name.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to update profile');
      }
      
      // Update the original name to reflect saved state
      setOriginalName(profileData.name.trim());
      setSavedMessage('Profile updated successfully!');
      
      // Emit a custom event to notify other components (like TopBar) that user data has changed
      window.dispatchEvent(new CustomEvent('userProfileUpdated', {
        detail: {
          name: profileData.name.trim(),
          email: profileData.email
        }
      }));
      
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(`Failed to update profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setProfileData(prev => ({
      ...prev,
      name: originalName
    }));
    setError('');
    setSavedMessage('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
              <p className="text-gray-600">Manage your personal profile information</p>
            </div>
          </div>
          
          {/* Context Info */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm">
                <p className="font-medium text-indigo-900">Current Context</p>
                <p className="text-indigo-700">
                  <span className="font-medium">Organization:</span> {currentOrganization?.name || 'Loading...'}
                  {currentSchool && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="font-medium">School:</span> {currentSchool.name}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          {savedMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-800 font-medium">{savedMessage}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Profile Information Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Profile Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Update your personal information and profile details.
            </p>
          </div>
          
          <div className="px-6 py-6">
            {/* Current Profile Display */}
            <div className="flex items-center gap-6 mb-8 pb-6 border-b border-gray-100">
              <img
                src={user?.picture || "https://randomuser.me/api/portraits/men/14.jpg"}
                alt="Profile"
                className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
              />
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">{profileData.name || 'User'}</h3>
                <p className="text-gray-600 mb-2">{profileData.email || 'user@example.com'}</p>
                <p className="text-sm text-gray-500">
                  Last login: {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={profileData.name}
                  onChange={handleNameChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  placeholder="Enter your full name"
                  disabled={saving}
                />
                <p className="mt-1 text-sm text-gray-500">
                  This name will be displayed throughout the application and visible to other users in your organization.
                </p>
              </div>

              {/* Email Field (Read-only) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={profileData.email}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  disabled
                  readOnly
                />
                <p className="mt-1 text-sm text-gray-500">
                  Email address cannot be changed here. Contact your administrator if you need to update your email.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-6 mt-8 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    hasChanges && !saving
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {saving ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>
                
                {hasChanges && (
                  <button
                    onClick={handleReset}
                    disabled={saving}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Reset
                  </button>
                )}
              </div>

              {hasChanges && (
                <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    You have unsaved changes
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}